import { c as _c } from "react/compiler-runtime";
import { useApolloClient, useQuery } from '@apollo/client/react';
import { Box, Text, VStack } from '@chakra-ui/react';
import { Segmented } from '@src/components/sco/Advisors/shared/Segmented';
import { formatWhen } from '@src/components/sco/Advisors/utils/sessionFormat';
import { ChatThread } from '@src/components/sco/Chat/ChatThread';
import { ADVISOR_SESSION_CHAT } from '@src/gql/queries/AdvisorSessionChat';
import { ADVISORS_ATTENTION } from '@src/gql/queries/AdvisorsAttention';
import { ScoAdvisorSession } from '@src/graphql-types';
import { ReactNode, useEffect, useEffectEvent, useState } from 'react';

/** Give the thread read (which clears its unread count server-side) time to land before refreshing badges. */
const BADGE_REFRESH_DELAY_MS = 1500;
export const SESSION_CHAT_TAB = 'This session';
export const DIRECT_CHAT_TAB = 'Messages';
export type SessionChatTab = typeof SESSION_CHAT_TAB | typeof DIRECT_CHAT_TAB;

/** Session thread id, preferring the resolved thread. Null for legacy rows whose hold never authorised. */
export const sessionChatThreadId = (session?: Pick<ScoAdvisorSession, 'chatThreadId' | 'chatThread'> | null) => session?.chatThread?.threadId || session?.chatThreadId || null;

/** The existing DM between the viewer and the other party (never created here). */
export const counterpartDirectThreadId = (session?: Pick<ScoAdvisorSession, 'currentUserRole' | 'expertUser' | 'seekerUser'> | null) => {
  if (!session) {
    return null;
  }
  const counterpart = session.currentUserRole === 'EXPERT' ? session.seekerUser : session.expertUser;
  return counterpart?.directChatThreadWithCurrentUser?.threadId || null;
};
export type SessionChatPanelViewProps = {
  sessionThreadId: string | null;
  directThreadId: string | null;
  title?: string;
  reason?: string | null;
  scheduledAt?: string | null;
  loading?: boolean;
  /** Override for stories; defaults to the shared `ChatThread`. */
  renderThread?: (threadId: string, tab: SessionChatTab) => ReactNode;
  /** Called when a thread is opened (and so marked read). */
  onThreadShown?: (threadId: string, tab: SessionChatTab) => void;
};
const defaultRenderThread = (threadId: string, tab: SessionChatTab) => <ChatThread key={threadId} hideHeader
// Session chat is scoped to the two parties by the session; DMs keep normal chat permissions.
bypassContentPermissions={tab === SESSION_CHAT_TAB} enableFileAttachments={false} threadId={threadId} />;

/**
 * L19: session chat with a "This session / Messages" toggle. Messages is the
 * pair's existing direct thread; the toggle is hidden when there is none.
 * Session chat stays out of Inbox and the Chat icon — it only lives here.
 */
export const SessionChatPanelView = t0 => {
  const $ = _c(35);
  const {
    sessionThreadId,
    directThreadId,
    title: t1,
    reason,
    scheduledAt,
    loading: t2,
    renderThread: t3,
    onThreadShown
  } = t0;
  const title = t1 === undefined ? "Session chat" : t1;
  const loading = t2 === undefined ? false : t2;
  const renderThread = t3 === undefined ? defaultRenderThread : t3;
  const [tab, setTab] = useState(SESSION_CHAT_TAB);
  const showToggle = !!directThreadId;
  const activeTab = showToggle ? tab : SESSION_CHAT_TAB;
  const threadId = activeTab === DIRECT_CHAT_TAB ? directThreadId : sessionThreadId;
  let t4;
  if ($[0] !== onThreadShown) {
    t4 = (shownThreadId, shownTab) => {
      onThreadShown?.(shownThreadId, shownTab);
    };
    $[0] = onThreadShown;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const notifyThreadShown = useEffectEvent(t4);
  let t5;
  if ($[2] !== activeTab || $[3] !== notifyThreadShown || $[4] !== threadId) {
    t5 = () => {
      if (threadId) {
        notifyThreadShown(threadId, activeTab);
      }
    };
    $[2] = activeTab;
    $[3] = notifyThreadShown;
    $[4] = threadId;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== activeTab || $[7] !== threadId) {
    t6 = [threadId, activeTab];
    $[6] = activeTab;
    $[7] = threadId;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== title) {
    t7 = <Text fontSize="sm" fontWeight="semibold">{title}</Text>;
    $[9] = title;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== reason) {
    t8 = reason ? <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="xs" lineClamp={2}>{reason}</Text> : null;
    $[11] = reason;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== scheduledAt) {
    t9 = scheduledAt ? <Text color="colorPalette.fg/50" fontSize="xs">{formatWhen(scheduledAt)}</Text> : null;
    $[13] = scheduledAt;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t10 = <Box>{t7}{t8}{t9}</Box>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== activeTab || $[20] !== showToggle) {
    t11 = showToggle ? <Segmented options={[SESSION_CHAT_TAB, DIRECT_CHAT_TAB]} size="xs" value={activeTab} onChange={value => setTab(value === DIRECT_CHAT_TAB ? DIRECT_CHAT_TAB : SESSION_CHAT_TAB)} /> : null;
    $[19] = activeTab;
    $[20] = showToggle;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <VStack align="stretch" borderBottomWidth="1px" flexShrink={0} gap={2} paddingX={3} paddingY={3}>{t10}{t11}</VStack>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== activeTab || $[26] !== loading || $[27] !== renderThread || $[28] !== threadId) {
    t13 = threadId ? renderThread(threadId, activeTab) : <Text color="colorPalette.fg/50" fontSize="sm" padding={3}>{loading ? "Loading chat\u2026" : "Chat is not available for this session."}</Text>;
    $[25] = activeTab;
    $[26] = loading;
    $[27] = renderThread;
    $[28] = threadId;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = <Box flex={1} minHeight={0}>{t13}</Box>;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t12 || $[33] !== t14) {
    t15 = <VStack align="stretch" gap={0} height="full" minHeight={0} width="full">{t12}{t14}</VStack>;
    $[32] = t12;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
};

/** Data-bound panel used by session detail and the room sidebar. */
export const SessionChatPanel = t0 => {
  const $ = _c(20);
  const {
    advisorSessionId,
    reason,
    scheduledAt
  } = t0;
  let t1;
  if ($[0] !== advisorSessionId) {
    t1 = {
      advisorSessionId
    };
    $[0] = advisorSessionId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !advisorSessionId;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      variables: t1,
      skip: t2,
      errorPolicy: "all"
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const query = useQuery(ADVISOR_SESSION_CHAT, t3);
  const session = query.data?.advisorSession;
  const client = useApolloClient();
  const [shownAt, setShownAt] = useState(0);
  let t4;
  let t5;
  if ($[5] !== client || $[6] !== shownAt) {
    t4 = () => {
      if (!shownAt) {
        return;
      }
      const timer = window.setTimeout(() => {
        client.refetchQueries({
          include: [ADVISORS_ATTENTION]
        }).catch(_temp);
      }, BADGE_REFRESH_DELAY_MS);
      return () => window.clearTimeout(timer);
    };
    t5 = [client, shownAt];
    $[5] = client;
    $[6] = shownAt;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== session) {
    t6 = counterpartDirectThreadId(session);
    $[9] = session;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = query.loading && !session;
  let t8;
  if ($[11] !== session) {
    t8 = sessionChatThreadId(session);
    $[11] = session;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setShownAt(Date.now());
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== reason || $[15] !== scheduledAt || $[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
    t10 = <SessionChatPanelView directThreadId={t6} loading={t7} reason={reason} scheduledAt={scheduledAt} sessionThreadId={t8} onThreadShown={t9} />;
    $[14] = reason;
    $[15] = scheduledAt;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
};
function _temp() {}